<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { listAdminMcpTokens, ApiError, type AdminMcpToken } from '@/api'
import { formatDate, ownerName } from '@/admin/format'
import DrillList from '@/components/DrillList.vue'

const { t } = useI18n()
const mcpTokens = ref<AdminMcpToken[]>([])
const mcpTokensLoading = ref(true)
const mcpTokensError = ref('')

function tokenStatus(token: AdminMcpToken): 'active' | 'revoked' | 'expired' {
  if (token.revoked_at) return 'revoked'
  if (token.expires_at && new Date(token.expires_at) < new Date()) return 'expired'
  return 'active'
}

// Owner; a level with one value is skipped.
const tokenLevels = [{ label: (token: AdminMcpToken) => ownerName(token.owner) }]
const tokenTexts = (token: AdminMcpToken) => [ownerName(token.owner), ...token.scopes]

async function loadMcpTokens() {
  mcpTokensLoading.value = true
  mcpTokensError.value = ''
  try {
    mcpTokens.value = await listAdminMcpTokens()
  } catch (e) {
    mcpTokensError.value = e instanceof ApiError ? e.message : t('admin.loadTokensFailed')
  } finally {
    mcpTokensLoading.value = false
  }
}

onMounted(loadMcpTokens)
</script>

<template>
  <p class="hint">{{ t('admin.mcpHint') }}</p>
  <p v-if="mcpTokensLoading">{{ t('common.loading') }}</p>
  <p v-else-if="mcpTokensError" class="error">{{ mcpTokensError }}</p>
  <p v-else-if="mcpTokens.length === 0" class="hint">{{ t('admin.noTokens') }}</p>
  <DrillList
    v-else
    :items="mcpTokens"
    :levels="tokenLevels"
    :texts="tokenTexts"
    :item-key="(token: AdminMcpToken) => mcpTokens.indexOf(token)"
    query-key="tokens"
    :placeholder="t('drill.search')"
  >
    <template #item="{ item: token }">
      <dl>
        <div class="field">
          <dt>{{ t('admin.col.owner') }}</dt>
          <dd>{{ ownerName(token.owner) }}</dd>
        </div>
        <div class="field">
          <dt>{{ t('admin.col.scopes') }}</dt>
          <dd class="chips">
            <span v-for="scope in token.scopes" :key="scope" class="ui-chip">{{ scope }}</span>
          </dd>
        </div>
        <div class="field">
          <dt>{{ t('admin.col.created') }}</dt>
          <dd>{{ formatDate(token.created_at) }}</dd>
        </div>
        <div class="field">
          <dt>{{ t('admin.col.expires') }}</dt>
          <dd>{{ token.expires_at ? formatDate(token.expires_at) : '—' }}</dd>
        </div>
        <div class="field">
          <dt>{{ t('admin.col.status') }}</dt>
          <dd>
            <span class="badge" :class="{ yes: tokenStatus(token) === 'active' }">{{ t(`admin.tokenStatus.${tokenStatus(token)}`) }}</span>
          </dd>
        </div>
      </dl>
    </template>
  </DrillList>
</template>

<style scoped>
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color);
  color: var(--tg-hint-color);
}

.badge.yes {
  color: #34c759;
}

.hint {
  color: var(--tg-hint-color);
  font-size: 0.85rem;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
